const d3 = require('d3')
const moment = require('moment');
const { parseDate } = require('../utils/formatters.js');
const { wrap } = require( '../utils/shared-functions.js');
const { TRANSLATE_GENERAL_TAG } = require('../consts.js');

const TRANSLATE_TAG = 'charts.night_respiratory'
const EMPTY_COLOR = '#000000';
const CIRCLE_COLOR = '#777B7F';
const EMPTY_BAR_WIDTH = 35;
const margin = { top: 40, right: 30, bottom: 40, left: 50 };
let MIN_YAXIS = 0;
let MAX_YAXIS = 20;

exports.createNightRespiratoryChart = function (rawData, thresholds, onClick, width, height, translate) {
  const data = rawData.map((d) => {
      const date = parseDate(moment(d['date']).format('YYYY-MM-DD'));
      const { value } = d;

      const empty = !value;
      return { date, value: +value?.toFixed(1), empty};
    });

  const x = d3
    .scaleBand()
    .domain(getDomainX(data))
    .range([margin.left, width - margin.right])
    .padding(0.4);

  const y = d3
    .scaleLinear()
    .domain([MIN_YAXIS, MAX_YAXIS])
    .nice()
    .range([height - margin.bottom, margin.top]);
  
  const xAxis = (g) =>
    g.attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x)
          .tickValues(x.domain())
          .tickFormat(d => getTickFormat(d))
          .tickPadding(0)
          .tickSize(0)
          .tickSizeOuter(0)
      )
      .style('font-size', '11px')
      .call((g) =>
        g.selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('transform', `translate(0,${-height + margin.top + margin.bottom})`)
          .attr('y2', height - margin.top - margin.bottom)
      )
      .call((g) => g.select('.domain').remove());

  const yAxis = (g) =>
    g
      .attr('transform', `translate(${margin.left - 10},0)`)
      .call(d3
          .axisLeft(y)
          .tickValues(d3.scaleLinear().domain(y.domain()).ticks())
          .tickFormat(d => d !== 0 ? d : '')
          .tickSize(0)
          .tickPadding(10)
      )
      .call((g) =>
        g.selectAll('.tick text') // Select the text elements of ticks
          .attr('opacity', 0.70)
          .style('font-size', '11px')
        )

        .call((g) =>
          g.selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('x2', width - margin.left - margin.right)
      )
      .call((g) => g.select('.domain').remove());
  
  const oxygenLineGenerate = d3
    .line()
    .curve(d3.curveBumpX)
    .defined((d) => d.value)
    .x((d) => x(d.date) + x.bandwidth() / 2)
    .y((d) => y(d.value));

  const svg = d3
    .create('svg')
    .attr('width', width)
    .attr('height', height);

  const oxygenLine = svg
    .append('path')
    .attr('d', oxygenLineGenerate(data))
    .attr('fill', 'none')
    .attr('stroke', CIRCLE_COLOR);

  svg.append('g').call(xAxis).selectAll('.tick text').call(wrap, x.bandwidth());
  svg.append('g').call(yAxis);

  const yAxisLabelUnit = svg
    .append('text')
    .attr('x', margin.left - 40)
    .attr('y', margin.top - 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.units.rpm`) : 'rpm');
  
  const xAxisLabelDate = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 20)
    .style('text-transform', 'capitalize')
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.date`) : 'Date');

  const xAxisLabelDay = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 33)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.day`) : 'Day')

  const g = svg
    .append('g')
    .selectAll('g')
    .data(data)
    .join('g')
    .attr('transform', (d) => `translate(${x(d.date) + x.bandwidth() / 2},0)`);

  const emptyGroup = g.append('g').attr('visibility', (d) => {
    return d.empty ? 'visible' : 'hidden';
  });

  const emptyBar = emptyGroup
    .append('line')
    .attr('y1', (d) => y(MIN_YAXIS))
    .attr('y2', (d) => y(MAX_YAXIS))
    .attr('stroke-width', EMPTY_BAR_WIDTH)
    .attr('stroke', (d) => EMPTY_COLOR)
    .attr('opacity', 0.07)

  const oxygenCandleGroup = g
    .append('g')
    .filter((d) => d.value)
 
  const oxygenCircle = oxygenCandleGroup
    .append('circle')
    .attr('r', 10)
    .attr('cy', (d) => y(d.value))
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', CIRCLE_COLOR)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const oxygenCircleText = oxygenCandleGroup
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', (d) => y(d.value) + 5)
    .style('font-size', '11px')
    .attr('font-weight', '400')
    .append('tspan')
    .attr('font-family', 'roboto')
    .text((d) => d.value)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

    const gLegend = svg.append('g').attr('transform', `translate(${width - margin.right}, ${margin.top - 25})`);
 
    const noMeasurementsLegendCircle = gLegend
    .append('rect')
    .attr('x', -675)
    .attr('y', 1)
    .attr('width', 15)
    .attr('height', 15)
    .attr('opacity', 0.11)
    .attr('fill', '#000000');

  const noMeasurementsLegendText = gLegend
    .append('text')
    .attr('x', -650)
    .attr('y', margin.top - 25)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.no_measure`) : 'No measurements');
     

  const valueLegend = gLegend
  .append('circle')
  .attr('cx', -465)
  .attr('cy', margin.top - 31)
  .attr('r', 9)
  .attr('stroke-width', 1)
  .attr('fill', 'white')
  .attr('stroke', '#3D9ADA');

  const valuePlaceHolder = gLegend
    .append('text')
    .attr('x', -469)
    .attr('y', margin.top - 26)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text('#');
     

const ppgLegendCircleText = gLegend
  .append('text')
  .attr('x', -445)
  .attr('y', margin.top - 25)
  .style('font-size', '17px')
  .attr('font-weight', '100')
  .text(translate ? translate(`${TRANSLATE_TAG}.title`) : 'Respiratory Rate During Night');
  

const deviceLegendText = gLegend
  .append('text')
  .attr('x', 0)
  .attr('y', margin.top - 25)
  .style('font-size', '17px')
  .style('direction', 'rtl')
  .attr('font-weight', '100')
  .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.device_measure`) : 'Device measurements');

  const deviceLegendCircle = gLegend
  .append('rect')
  .attr('x', -190)
  .attr('y', 1)
  .attr('width', 15)
  .attr('height', 15)
  .attr('fill', CIRCLE_COLOR)

  return svg.node();
};

function getDomainX(data) {
  return d3.utcDay.range(data[0].date, +data[data.length - 1].date + 1)
}

function getTickFormat(date) {
  return moment(date).format('ddd DD')
}

